import './style.scss';

import React, { useEffect, useState } from 'react';
import { Col, Form, InputGroup, Row } from 'react-bootstrap-v5';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';

import { MODE, TYPE_TEXT, WORKING_TIME_LIST } from '../../../../../../utils/common/constants';
import { SelectFilter } from '../../../../../../utils/common/models';
import { convertOperationObjectToList } from '../../../../../../utils/helpers';
import { TimeInWeek, ValueItemTimeInWeek } from '../../../../models';
import { OperationHoursAndMap } from '../../../../models/OperationHoursAndMap';
import moment from 'moment';

type Props = {
    isRamadan: boolean,
    rtl?: boolean,
    timeInWeek: TimeInWeek[],
    operationHoursAndMap: OperationHoursAndMap[],
    updateValueOperationHours: (time: TimeInWeek[]) => void,
    mode?: string
    overnightCfg?: {
        endTime: number
        isActive: boolean
    }
}

export interface SelectTime {
    title: string
    value: number
}

function coerceMinute(v: string | number | undefined | null): number {
    if (v === undefined || v === null) return 0
    return typeof v === 'string' ? parseFloat(v) : v
}

/** Persist min start from previous calendar day spill (overnight) and from prior range same day — fixes copy/save when UI showed adjusted times but Formik still had 0. */
function syncOperationHourStartsAcrossWeek(list: TimeInWeek[]): TimeInWeek[] {
    let current = list.map((d) => ({
        ...d,
        value: d.value.map((r) => ({ ...r })),
    }))
    const len = current.length
    if (!len) return current

    for (let iter = 0; iter < 8; iter++) {
        let changed = false
        const working = current.map((d) => ({
            ...d,
            value: d.value.map((r) => ({ ...r })),
        }))

        for (let dayIdx = 0; dayIdx < len; dayIdx++) {
            const day = working[dayIdx]
            if (!day.isSelected || !day.value?.length) continue

            const prevDayIdx = dayIdx === 0 ? len - 1 : dayIdx - 1
            const prevDay = working[prevDayIdx]
            let minFromPrevDay = 0
            if (prevDay?.isSelected && prevDay.value?.length) {
                minFromPrevDay = prevDay.value.reduce((maxCarry, slot) => {
                    const end = coerceMinute(slot.end)
                    return end > 1440 ? Math.max(maxCarry, end - 1440) : maxCarry
                }, 0)
            }

            const newRanges: ValueItemTimeInWeek[] = []
            for (let r = 0; r < day.value.length; r++) {
                const range = day.value[r]
                let minStart = minFromPrevDay
                if (r > 0) {
                    minStart = Math.max(minStart, coerceMinute(newRanges[r - 1].end))
                }
                const start0 = coerceMinute(range.start)
                const end0 = coerceMinute(range.end)
                const start = Math.max(start0, minStart)
                const end = Math.max(end0, start)
                if (start !== start0 || end !== end0) changed = true
                newRanges.push({ ...range, start, end })
            }
            working[dayIdx] = { ...day, value: newRanges }
        }

        current = working
        if (!changed) break
    }

    return current
}

const ManagementTimeInWeek: React.FC<Props> = ({ operationHoursAndMap, timeInWeek, rtl, isRamadan, updateValueOperationHours, mode, overnightCfg }) => {
    const { t } = useTranslation();
    const baseWorkingTimes = WORKING_TIME_LIST;
    const maxExtraMinutes = overnightCfg?.isActive ? Math.max(0, overnightCfg.endTime || 0) : 0;
    const maxMinuteValue = baseWorkingTimes[baseWorkingTimes.length - 1].value + maxExtraMinutes;

    const workingTimeList: SelectTime[] = React.useMemo(() => {
        if (maxExtraMinutes <= 0) return baseWorkingTimes as any;
        const last = baseWorkingTimes[baseWorkingTimes.length - 1]?.value ?? 1440;
        const extended: SelectTime[] = [...(baseWorkingTimes as any)];
        for (let v = last + 30; v <= maxMinuteValue; v += 30) {
            extended.push({ title: '', value: v });
        }
        return extended;
    }, [baseWorkingTimes, maxExtraMinutes, maxMinuteValue]);

    const endDayValue = workingTimeList[workingTimeList.length - 1].value;
    const [optionSelectOperationHoursCP, setOptionSelectOperationHoursCP] = useState<SelectFilter[]>([])

    const getMinStartFromPreviousDay = (dayIdx: number) => {
        if (!timeInWeek.length) return 0
        const previousDayIndex = dayIdx === 0 ? timeInWeek.length - 1 : dayIdx - 1
        const previousDay = timeInWeek[previousDayIndex]
        if (!previousDay?.isSelected || !previousDay.value?.length) return 0

        const carriedMinutes = previousDay.value.reduce((maxCarry, slot) => {
            const end = typeof slot.end === 'string' ? parseFloat(slot.end) : slot.end
            if (end > 1440) {
                return Math.max(maxCarry, end - 1440)
            }
            return maxCarry
        }, 0)

        return carriedMinutes
    }

    const getMinStartByRange = (dayIdx: number, rangeIdx: number) => {
        const minFromPreviousDay = getMinStartFromPreviousDay(dayIdx)
        if (rangeIdx <= 0) return minFromPreviousDay

        const previousRange = timeInWeek?.[dayIdx]?.value?.[rangeIdx - 1]
        const previousRangeEnd = typeof previousRange?.end === 'string' ? parseFloat(previousRange.end) : (previousRange?.end ?? 0)
        return Math.max(minFromPreviousDay, previousRangeEnd)
    }

    const getTextFromType = (type: number) => {
        switch (type) {
            case TYPE_TEXT.TITLE:
                return mode === MODE.CLUSTER ? t('ADD_DISPATCH_MODAL.COPY_FROM_ANOTHER_CLUSTER') : t('USERS_PAGE.CONTENT_STEP.STEP4.TITLE_SELECT_TECH')
            case TYPE_TEXT.PLACE_HOLDER:
                return mode === MODE.CLUSTER ? t('ADD_DISPATCH_MODAL.SELECT_CLUSTER') : t('USERS_PAGE.CONTENT_STEP.STEP5.SELECT_TECHNICIAN')
        }
    }
    const [operationHoursCpSelected, setOperationHoursCpSelected] = useState<SelectFilter>({ label: getTextFromType(TYPE_TEXT.PLACE_HOLDER) || "", value: '' });

    // Select day on/off
    const selectWorkDay = (data: TimeInWeek) => {
        const newList = timeInWeek.map((item) =>
            item.key === data.key
                ? { ...item, isSelected: !item.isSelected }
                : item
        );
        updateValueOperationHours(syncOperationHourStartsAcrossWeek(newList));
    };

    // Change time on Day
    const changeStartTimeOnDay = (event: any, idx: number, dayIdx: number) => {
        const value = Number(event.target.value);
        const minStartValue = getMinStartByRange(dayIdx, idx);
        const normalizedValue = Math.max(value, minStartValue)

        const newList = timeInWeek.map((day, i) =>
            i === dayIdx
                ? {
                    ...day,
                    value: day.value.map((time, j) =>
                        j === idx ? { ...time, start: normalizedValue } : time
                    ),
                }
                : day
        );

        updateValueOperationHours(newList);
    };

    const changeEndTimeOnDay = (event: any, idx: number, dayIdx: number) => {
        const value = Number(event.target.value);

        const newList = timeInWeek.map((day, i) =>
            i === dayIdx
                ? {
                    ...day,
                    value: day.value.map((time, j) =>
                        j === idx ? { ...time, end: value } : time
                    ),
                }
                : day
        );

        updateValueOperationHours(syncOperationHourStartsAcrossWeek(newList));
    };
    // End Change time on Day

    // Create and remove time on Day
    const createNewTimeOnDay = (dayIdx: number) => {
        const lastEndRaw = timeInWeek?.[dayIdx]?.value?.[timeInWeek?.[dayIdx]?.value?.length - 1]?.end
        const lastEnd = typeof lastEndRaw === "string" ? parseFloat(lastEndRaw) : (lastEndRaw ?? 0)
        // If previous range ends next day, do not allow creating another range.
        if (lastEnd > 1440) return

        const newList = timeInWeek.map((day, i) =>
            i === dayIdx
                ? {
                    ...day,
                    value: [
                        ...day.value,
                        {
                            start: lastEnd,
                            end: Math.max(lastEnd, endDayValue),
                        },
                    ],
                }
                : day
        );

        updateValueOperationHours(syncOperationHourStartsAcrossWeek(newList));
    };

    const removeNewTimeOnDay = (idx: number, dayIdx: number) => {
        const newList = timeInWeek.map((day, i) =>
            i === dayIdx
                ? {
                    ...day,
                    value: day.value.filter((_, j) => j !== idx),
                }
                : day
        );
        updateValueOperationHours(syncOperationHourStartsAcrossWeek(newList));
    };

    const handleCopyHours = (data: TimeInWeek) => {
        const copiedValue = data.value.map(x => ({ ...x })); // deep clone

        const newList = timeInWeek.map(day =>
            day.isSelected ? { ...day, value: copiedValue.map(x => ({ ...x })) } : day
        );

        updateValueOperationHours(syncOperationHourStartsAcrossWeek(newList));
    };


    const changeSelectOperationHoursCP = (item: any) => {
        setOperationHoursCpSelected(item);
        if (!item.value || operationHoursAndMap.length === 0) return
        let operationHoursMap: OperationHoursAndMap | undefined = operationHoursAndMap.find((data) => data.id === item.value) || undefined
        if (!operationHoursMap) return
        let operationHours = timeInWeek;
        if (isRamadan) {
            let timeObject = operationHoursMap.ramadanOperationHours && JSON.parse(operationHoursMap.ramadanOperationHours);
            operationHours = convertOperationObjectToList(timeObject);
        } else {
            let timeObject = operationHoursMap.operationHours && JSON.parse(operationHoursMap.operationHours);
            operationHours = convertOperationObjectToList(timeObject);
        }
        updateValueOperationHours(syncOperationHourStartsAcrossWeek(operationHours));
    }

    useEffect(() => {
        const listFilter: SelectFilter[] = [{ label: getTextFromType(TYPE_TEXT.PLACE_HOLDER) || "", value: "", isDisabled: true }]
        if (operationHoursAndMap) {
            operationHoursAndMap.forEach((item: OperationHoursAndMap) => {
                listFilter.push({
                    value: item.id || '',
                    label: rtl ? item.fullNameAr || '' : item.fullNameEn || '',
                })
            });
        }
        setOptionSelectOperationHoursCP(listFilter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [operationHoursAndMap, rtl])



    return (
        <Row className='justify-content-center'>
            <Col sm={12} md={10}>
                <div className='select-copy-operation-hours mb-10'>
                    <div className='title-select'>{getTextFromType(TYPE_TEXT.TITLE)}</div>
                    <InputGroup className='filter-bus-copy'>
                        <Select
                            className='select-input'
                            classNamePrefix='select'
                            name='color'
                            placeholder={getTextFromType(TYPE_TEXT.PLACE_HOLDER)}
                            options={optionSelectOperationHoursCP}
                            onChange={changeSelectOperationHoursCP}
                            value={operationHoursCpSelected}

                        />
                    </InputGroup>
                </div>
                {timeInWeek.length > 0 &&
                    timeInWeek.map((dataTimeInWeek: TimeInWeek, indexRoot: number) => (
                        <Row className='box-day-select' key={indexRoot}>
                            <Col sm={12} md={12} lg={3} className='title'>
                                <div className='mt-4'>
                                    <label className='form-check form-switch form-switch-sm form-check-custom form-check-solid select-toggle' style={{ width: 'max-content' }}>
                                        <span className='title-day mx-4'>{t(`${dataTimeInWeek.label}`)}</span>
                                        <input className='form-check-input' type='checkbox' checked={dataTimeInWeek.isSelected} onChange={() => selectWorkDay(dataTimeInWeek)} />
                                    </label>
                                </div>
                            </Col>
                            <Col sm={12} md={12} lg={9} className={`select-times ${!dataTimeInWeek.isSelected ? 'disable-none-select-day' : ''}`}>
                                {dataTimeInWeek.value && dataTimeInWeek.value.map((time: ValueItemTimeInWeek, indexChild: number) => (
                                    <Row key={indexChild} className='mt-4'>
                                        <Col sm={12} md={6} lg={4} className="mb-3">
                                            <Form.Select
                                                onChange={(event: any) => changeStartTimeOnDay(event, indexChild, indexRoot)}
                                                value={time.start}
                                            >
                                                {baseWorkingTimes.map((timeWorking: SelectTime, indexTimeWorking) => {
                                                    const label = `${moment()
                                                        .startOf('day')
                                                        .locale(rtl ? 'ar-SA' : 'en-US')
                                                        .add(timeWorking.value % 1440, 'minute')
                                                        .format('hh:mm A')}`

                                                    return (
                                                        <option
                                                            key={indexTimeWorking}
                                                            value={timeWorking.value}
                                                            disabled={
                                                                timeWorking.value < getMinStartByRange(indexRoot, indexChild) ||
                                                                timeWorking.value >=
                                                                (typeof time.end === "string" ? parseFloat(time.end) : time.end)
                                                            }
                                                        >
                                                            {label}
                                                        </option>
                                                    )
                                                })}
                                            </Form.Select>
                                        </Col>
                                        <Col sm={12} md={6} lg={4} className="mb-3">
                                            <Form.Select
                                                onChange={(event: any) => changeEndTimeOnDay(event, indexChild, indexRoot)}
                                                value={time.end}
                                            >
                                                {workingTimeList.map((timeWorking: SelectTime) => (
                                                    <option key={timeWorking.value} value={timeWorking.value} disabled={timeWorking.value <= (typeof time.start === "string" ? parseFloat(time.start) : time.start)}>
                                                        {`${moment()
                                                            .startOf('day')
                                                            .locale(rtl ? 'ar-SA' : 'en-US')
                                                            .add(timeWorking.value % 1440, 'minute')
                                                            .format('hh:mm A')}${maxExtraMinutes > 0 && timeWorking.value > 1440 ? t('NEXT_DAY') : ''}`}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Col>
                                        <Col sm={12} md={12} lg={4} className='box-action-time mb-3'>
                                            <Row className="w-100 justify-content-between">
                                                <Col sm={4} className="w-50px">
                                                    <button type='button' className='btn action-time mx-0'
                                                        disabled={dataTimeInWeek.value.length <= 1}
                                                        onClick={() => removeNewTimeOnDay(indexChild, indexRoot)}>
                                                        <i className="bi bi-trash icon-delete-hour"></i>
                                                    </button>
                                                </Col>
                                                <Col sm={4} className="w-50px">
                                                    {(dataTimeInWeek.value.length - 1) === indexChild && <div className='d-flex'>
                                                        <button
                                                            type='button'
                                                            className='btn action-time mx-2'
                                                            onClick={() => createNewTimeOnDay(indexRoot)}
                                                            disabled={
                                                                (() => {
                                                                    const endRaw = time.end
                                                                    const endValue = typeof endRaw === 'string' ? parseFloat(endRaw) : endRaw
                                                                    return endValue > 1440 || endDayValue === endValue
                                                                })()
                                                            }
                                                        >
                                                            <i className="bi bi-plus-square-fill icon-add-hour"></i>
                                                        </button>
                                                    </div>
                                                    }
                                                </Col>
                                                <Col sm={4} className="w-50px">
                                                    {indexRoot === 0 && indexChild === 0 &&
                                                        <button type='button' className='btn action-time mx-2' onClick={() => handleCopyHours(dataTimeInWeek)}>
                                                            <i className="far fa-clone icon-copy-hour"></i>
                                                        </button>
                                                    }
                                                </Col>
                                            </Row>
                                        </Col>
                                    </Row>
                                ))}
                            </Col>
                        </Row>
                    ))}
            </Col>
        </Row>

    )
}

export default ManagementTimeInWeek


